import React from 'react';

interface PackingSlipItem {
  product_name: string;
  description: string;
  quantity: number;
  unit: string;
  total_lbs: number;
  bag_size: number;
  is_promo?: boolean;
  // Phase 3 unified bag size (falls back to legacy bag_size when absent)
  display_bag_size_lbs?: number;
}

interface PackingSlipData {
  company: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    email: string;
  };
  customer: {
    company_name: string;
    contact_name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    email: string;
  };
  shipping: {
    company_name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  invoice: {
    invoice_number: string;
    invoice_date: string;
  };
  order: {
    order_number: string;
    delivery_date: string | null;
    notes: string;
    special_instructions: string;
  };
  line_items: PackingSlipItem[];
}

interface Props {
  data: PackingSlipData;
  onClose?: () => void;
}

const PrintablePackingSlip: React.FC<Props> = ({ data, onClose }) => {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const totalBags = data.line_items.reduce((sum, item) =>
    sum + (item.unit === 'lbs' ? 0 : item.quantity), 0);
  const totalWeight = data.line_items.reduce((sum, item) => sum + item.total_lbs, 0);

  return (
    <div style={styles.container}>
      {/* Print controls */}
      <div style={styles.printControls} className="no-print">
        <button onClick={() => window.print()} style={styles.printButton}>
          Print Packing Slip
        </button>
        {onClose && (
          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        )}
      </div>

      {/* Packing slip document */}
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.companyInfo}>
            <h1 style={styles.companyName}>{data.company.name}</h1>
            <p style={styles.companyAddress}>
              {data.company.address}<br />
              {data.company.city}, {data.company.state} {data.company.zip}
            </p>
            <p style={styles.companyContact}>
              {data.company.phone}<br />
              {data.company.email}
            </p>
          </div>
          <div style={styles.titleBlock}>
            <h2 style={styles.titleLabel}>PACKING SLIP</h2>
            <p style={styles.titleNumber}>{data.invoice.invoice_number}</p>
          </div>
        </div>

        {/* Addresses */}
        <div style={styles.addressSection}>
          <div style={styles.addressBlock}>
            <h3 style={styles.addressLabel}>BILL TO:</h3>
            <p style={styles.addressName}>{data.customer.company_name}</p>
            {data.customer.contact_name && (
              <p style={styles.addressLine}>Attn: {data.customer.contact_name}</p>
            )}
            <p style={styles.addressLine}>{data.customer.address}</p>
            <p style={styles.addressLine}>
              {data.customer.city}, {data.customer.state} {data.customer.zip}
            </p>
          </div>
          <div style={styles.addressBlock}>
            <h3 style={styles.addressLabel}>SHIP TO:</h3>
            <p style={styles.addressName}>{data.shipping.company_name}</p>
            <p style={styles.addressLine}>{data.shipping.address}</p>
            <p style={styles.addressLine}>
              {data.shipping.city}, {data.shipping.state} {data.shipping.zip}
            </p>
          </div>
        </div>

        {/* Order details */}
        <div style={styles.detailsSection}>
          <div style={styles.detailsGrid}>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Order #:</span>
              <span style={styles.detailValue}>{data.order.order_number}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Invoice Date:</span>
              <span style={styles.detailValue}>{formatDate(data.invoice.invoice_date)}</span>
            </div>
            {data.order.delivery_date && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Delivery Date:</span>
                <span style={styles.detailValue}>{formatDate(data.order.delivery_date)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Line items — no pricing */}
        <div style={styles.lineItemsSection}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.thProduct}>Product</th>
                <th style={styles.thCenter}>Qty</th>
                <th style={styles.thCenter}>Bag Size</th>
                <th style={styles.thRight}>Total Lbs</th>
              </tr>
            </thead>
            <tbody>
              {data.line_items.map((item, index) => (
                <tr key={index}>
                  <td style={styles.tdProduct}>
                    <strong>{item.product_name}</strong>
                    {item.description && (
                      <><br /><span style={styles.itemDesc}>{item.description}</span></>
                    )}
                    {item.is_promo && (
                      <span style={styles.promoTag}> PROMO</span>
                    )}
                  </td>
                  <td style={styles.tdCenter}>
                    {item.quantity} {item.unit}
                  </td>
                  <td style={styles.tdCenter}>
                    {item.unit === 'lbs' ? '-' : `${item.display_bag_size_lbs ?? item.bag_size}lb`}
                  </td>
                  <td style={styles.tdRight}>
                    {item.total_lbs.toLocaleString()} lbs
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals summary */}
        <div style={styles.summarySection}>
          {totalBags > 0 && (
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Total Bags:</span>
              <span style={styles.summaryValue}>{totalBags}</span>
            </div>
          )}
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Total Weight:</span>
            <span style={styles.summaryValue}>{totalWeight.toLocaleString()} lbs</span>
          </div>
        </div>

        {/* Notes */}
        {(data.order.notes || data.order.special_instructions) && (
          <div style={styles.notesSection}>
            <h3 style={styles.notesLabel}>NOTES:</h3>
            <p style={styles.notesText}>
              {data.order.special_instructions || data.order.notes}
            </p>
          </div>
        )}

        {/* Signature line */}
        <div style={styles.signatureSection}>
          <div style={styles.signatureLine}>
            <span>Received by: </span>
            <span style={styles.signatureUnderline}></span>
          </div>
          <div style={styles.signatureLine}>
            <span>Date: </span>
            <span style={styles.signatureUnderline}></span>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          /* Hide global nav header */
          .rbf-header {
            display: none !important;
          }
          body {
            margin: 0;
            padding: 0;
          }
        }
      `}</style>
    </div>
  );
};

const thBase: React.CSSProperties = {
  padding: '0.75rem 0.5rem',
  borderBottom: '2px solid #1f2937',
  fontSize: '0.8rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontFamily: 'Arial, sans-serif',
};

const tdBase: React.CSSProperties = {
  padding: '0.75rem 0.5rem',
  borderBottom: '1px solid #e5e7eb',
  verticalAlign: 'top',
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#f3f4f6',
    minHeight: '100%',
    padding: '1rem',
  },
  printControls: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    marginBottom: '1rem',
  },
  printButton: {
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: 600,
    backgroundColor: '#2D5A27',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  closeButton: {
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: 500,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  page: {
    backgroundColor: 'white',
    maxWidth: '800px',
    margin: '0 auto',
    padding: '2rem',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    fontFamily: 'Georgia, serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '3px solid #8B0000',
    paddingBottom: '1.5rem',
    marginBottom: '1.5rem',
  },
  companyInfo: { flex: 1 },
  companyName: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#8B0000',
    margin: '0 0 0.5rem 0',
    fontFamily: 'Arial, sans-serif',
  },
  companyAddress: {
    margin: '0 0 0.25rem 0',
    fontSize: '0.9rem',
    color: '#4b5563',
    lineHeight: 1.4,
  },
  companyContact: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#4b5563',
    lineHeight: 1.4,
  },
  titleBlock: { textAlign: 'right' as const },
  titleLabel: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#1f2937',
    margin: '0 0 0.25rem 0',
    fontFamily: 'Arial, sans-serif',
  },
  titleNumber: {
    fontSize: '1.1rem',
    color: '#4b5563',
    margin: 0,
    fontFamily: 'monospace',
  },
  addressSection: {
    display: 'flex',
    gap: '3rem',
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
  },
  addressBlock: { flex: 1 },
  addressLabel: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#6b7280',
    margin: '0 0 0.5rem 0',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontFamily: 'Arial, sans-serif',
  },
  addressName: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1f2937',
    margin: '0 0 0.25rem 0',
  },
  addressLine: {
    fontSize: '0.9rem',
    color: '#4b5563',
    margin: '0 0 0.125rem 0',
  },
  detailsSection: {
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.75rem 2rem',
  },
  detailItem: { display: 'flex', gap: '0.5rem' },
  detailLabel: { fontSize: '0.85rem', color: '#6b7280', fontWeight: 500 },
  detailValue: { fontSize: '0.85rem', color: '#1f2937' },
  lineItemsSection: { marginBottom: '1.5rem' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  thProduct: { ...thBase, textAlign: 'left' as const },
  thCenter: { ...thBase, textAlign: 'center' as const },
  thRight: { ...thBase, textAlign: 'right' as const },
  tdProduct: { ...tdBase },
  tdCenter: { ...tdBase, textAlign: 'center' as const },
  tdRight: { ...tdBase, textAlign: 'right' as const, fontWeight: 600 },
  itemDesc: { fontSize: '0.85rem', color: '#6b7280' },
  promoTag: {
    display: 'inline-block',
    padding: '0.125rem 0.375rem',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    fontSize: '0.7rem',
    fontWeight: 700,
    borderRadius: '3px',
    fontFamily: 'Arial, sans-serif',
    marginLeft: '0.5rem',
  },
  summarySection: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '2rem',
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
  },
  summaryRow: { display: 'flex', gap: '0.5rem', fontSize: '1rem' },
  summaryLabel: { fontWeight: 600, color: '#1f2937' },
  summaryValue: { fontWeight: 700, color: '#1f2937' },
  notesSection: {
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '4px',
  },
  notesLabel: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#6b7280',
    margin: '0 0 0.5rem 0',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontFamily: 'Arial, sans-serif',
  },
  notesText: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#4b5563',
    lineHeight: 1.5,
  },
  signatureSection: {
    display: 'flex',
    gap: '3rem',
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: '3px solid #8B0000',
  },
  signatureLine: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '0.5rem',
    fontSize: '0.9rem',
    color: '#4b5563',
  },
  signatureUnderline: {
    display: 'inline-block',
    width: '200px',
    borderBottom: '1px solid #1f2937',
    height: '1.2em',
  },
};

export default PrintablePackingSlip;
